/**
 * PersonProfile — canonical view of one real-world individual keyed by
 * SP-XXXXXXXXX.  Shows identity, every family they belong to (filtered
 * by viewer's read access), and a "How am I related to…?" search that
 * calls the relationship-path calculator.
 *
 * Route: /person/:spId
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import {
  ArrowLeft, Users, MapPin, Cake, Flower2, Loader2, GitBranch, Search, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

export default function PersonProfile() {
  const { spId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [rel, setRel] = useState(null);
  const [relBusy, setRelBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    http.get(`/persons/${spId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [spId]);

  const findRelationship = async () => {
    if (!query.trim()) return;
    setRelBusy(true);
    setRel(null);
    try {
      // Resolve query → sp_id.  Accepts full SP-XXXXXXXXX or "Firstname Lastname"
      // (in which case we look for exact match in persons collection first).
      let targetSp = query.trim();
      if (!/^SP-\d{9}$/i.test(targetSp)) {
        // Try to resolve name via a naive search.
        try {
          const r = await http.get(`/persons-search?q=${encodeURIComponent(targetSp)}`);
          if (r.data?.items?.length) targetSp = r.data.items[0].sp_id;
        } catch { /* fall through with raw text; server will 404 */ }
      }
      const r = await http.get(`/persons/${encodeURIComponent(spId)}/relationship-to/${encodeURIComponent(targetSp)}`);
      setRel(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute relationship");
    } finally {
      setRelBusy(false);
    }
  };

  if (loading) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return (
    <div className="max-w-xl mx-auto py-16 text-center">
      <p className="opacity-70">Person not found or not visible to you.</p>
      <Link to="/" className="wofy-btn-primary mt-4">Back home</Link>
    </div>
  );

  const p = data.person;
  const fullName = [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ") || "Unnamed";

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6" data-testid="person-profile">
      <Link to="/family" className="text-sm flex items-center gap-1 opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to family trees
      </Link>

      {/* Identity hero */}
      <div
        className="p-6 rounded-2xl border flex items-center gap-5 flex-wrap"
        style={{
          borderColor: "#4338CA33",
          background: "linear-gradient(135deg, #EEF2FF 0%, #FDF4FF 100%)",
        }}
      >
        {p.photo
          ? <img src={resolveImage(p.photo)} alt={fullName} className="w-24 h-24 rounded-full object-cover border" />
          : <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: "#4338CA22" }}>
              <Users className="w-10 h-10" style={{ color: "#4338CA" }} />
            </div>}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-mono uppercase tracking-widest opacity-60 flex items-center gap-2" data-testid="person-sp-id">
            SOCIAL PULSE PERSON
            <span className="px-1.5 py-0.5 rounded" style={{ background: "#4338CA22", color: "#4338CA" }}>{p.sp_id}</span>
          </div>
          <h1 className="font-display text-3xl mt-1" data-testid="person-name">{fullName}</h1>
          <div className="mt-1 text-sm opacity-80 flex items-center gap-4 flex-wrap">
            {p.birth_date && <span className="flex items-center gap-1"><Cake className="w-3.5 h-3.5" />{p.birth_date}</span>}
            {p.death_date && <span className="flex items-center gap-1"><Flower2 className="w-3.5 h-3.5" />{p.death_date}</span>}
            {(p.location?.city || p.location?.country) && (
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[p.location?.city, p.location?.country].filter(Boolean).join(", ")}</span>
            )}
          </div>
        </div>
      </div>

      {/* Families */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <GitBranch className="w-4 h-4" style={{ color: "#4338CA" }} />
          <h2 className="font-display text-xl">Families</h2>
          <span className="text-xs opacity-60">— {data.family_count} in your view</span>
        </div>
        {data.family_count === 0 ? (
          <div className="wofy-card text-center py-10 opacity-70">
            This person isn't visible in any families you have access to.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.families.map((f) => (
              <Link
                key={f.tree.id}
                to={`/family/${f.tree.id}/manage`}
                className="p-4 rounded-xl border hover:shadow-md transition"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`person-family-${f.tree.id}`}
              >
                <div className="flex items-center gap-3">
                  {f.tree.crest
                    ? <img src={resolveImage(f.tree.crest)} alt="" className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full" style={{ background: "#4338CA1A" }} />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{f.tree.name}</div>
                    {f.tree.fam_id && <div className="text-[10px] font-mono opacity-60">{f.tree.fam_id}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                    {f.role}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Relationship finder */}
      <div className="mt-8 wofy-card" data-testid="person-relationship-finder">
        <div className="flex items-center gap-2 mb-2">
          <Search className="w-4 h-4" style={{ color: "#4338CA" }} />
          <h3 className="font-display text-lg">How am I related to…?</h3>
        </div>
        <p className="text-xs opacity-70 mb-3">
          Paste another SP-ID (like SP-000000005) and we'll trace the shortest relationship path through the family graph.
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="SP-000000005"
            className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[220px]"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="person-rel-input"
            onKeyDown={(e) => e.key === "Enter" && findRelationship()}
          />
          <button onClick={findRelationship} disabled={relBusy || !query.trim()} className="wofy-btn-primary text-sm" data-testid="person-rel-go">
            {relBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find relationship"}
          </button>
        </div>

        {rel && (
          <div className="mt-4 p-4 rounded-lg border" style={{ borderColor: "var(--wofy-border)", background: rel.found ? "#EEF2FF" : "#FEF2F2" }} data-testid="person-rel-result">
            {!rel.found ? (
              <p className="text-sm">No known relationship path found within 8 steps.</p>
            ) : (
              <>
                <div className="text-lg font-display">
                  You are <span style={{ color: "#4338CA" }}>{rel.description}</span>
                  {rel.common_family && <> through <strong>{rel.common_family.name}</strong></>}
                </div>
                <div className="text-xs opacity-70 mt-1">
                  Distance: {rel.distance} step{rel.distance === 1 ? "" : "s"}
                </div>
                {rel.path?.length > 1 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
                    {rel.path.map((n, i) => (
                      <span key={i} className="flex items-center gap-2">
                        {i > 0 && (
                          <span className="text-[10px] opacity-60 flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" /> {n.edge}
                          </span>
                        )}
                        <Link to={`/person/${n.sp_id}`} className="underline" style={{ color: "#4338CA" }}>
                          {[n.first_name, n.last_name].filter(Boolean).join(" ") || n.sp_id}
                        </Link>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
